import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Gavel } from "lucide-react";

const VOTE_LABELS = { correct: "Correct", incorrect: "Incorrect", partial: "Partially", cannot_determine: "Can't tell" };

export default function ResolutionBox({ call, onChange }) {
  const { user } = useAuth();
  const [resolution, setResolution] = useState(null);
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState("called_it");
  const [explanation, setExplanation] = useState("");
  const [evidence, setEvidence] = useState("");
  const [busy, setBusy] = useState(false);

  const isOwner = user && call.author?.user_id === user.user_id;
  const isFinal = call.status !== "pending";

  const load = () => api.get(`/calls/${call.short_id}/resolution`).then(({ data }) => setResolution(data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [call.short_id, call.status]);

  const request = async () => {
    setBusy(true);
    try {
      await api.post(`/calls/${call.short_id}/resolution/request`, {
        outcome, explanation, evidence_urls: evidence.split(/\s+/).filter(Boolean),
      });
      toast.success("Resolution submitted");
      setOpen(false); onChange?.(); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally { setBusy(false); }
  };

  const vote = async (v) => {
    try {
      const { data } = await api.post(`/resolutions/${resolution.id}/vote`, { vote: v });
      setResolution((r) => ({ ...r, votes: data.votes }));
      toast.success("Vote recorded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not vote"); }
  };

  if (isFinal) {
    const r = call.resolution;
    if (!r?.evidence_urls?.length) return null;
    return (
      <div data-testid="resolution-evidence" className="card-brutal p-5 bg-white">
        <h3 className="font-display text-lg font-bold mb-2">Resolution evidence</h3>
        {r.explanation && <p className="text-sm mb-2">{r.explanation}</p>}
        <ul className="text-sm space-y-1">
          {r.evidence_urls.map((u, i) => (
            <li key={i}><a href={u} target="_blank" rel="noopener" className="text-brand underline break-all">{u}</a></li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div data-testid="resolution-box" className="card-brutal p-5 bg-white">
      <h3 className="font-display text-lg font-bold flex items-center gap-2 mb-1"><Gavel className="w-5 h-5" /> Resolution</h3>
      {resolution && resolution.status === "pending_review" ? (
        <div>
          <p className="text-sm text-muted-foreground mb-2">
            The author claims: <b>{resolution.outcome}</b>. Success claims are verified by the community and admins.
          </p>
          <p className="text-sm mb-3">{resolution.explanation}</p>
          {user && !isOwner ? (
            <div className="flex flex-wrap gap-2">
              {Object.keys(VOTE_LABELS).map((v) => (
                <button key={v} data-testid={`vote-${v}`} onClick={() => vote(v)}
                  className="h-9 px-3 border-2 border-ink bg-white text-sm font-bold btn-press shadow-brutal-sm">
                  {VOTE_LABELS[v]} {resolution.votes?.[v] ? `(${resolution.votes[v]})` : ""}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Awaiting community verification & admin review.</p>
          )}
        </div>
      ) : isOwner ? (
        <>
          <p className="text-sm text-muted-foreground mb-3">Did your Call land? Request resolution with evidence.</p>
          <button data-testid="request-resolution-btn" onClick={() => setOpen(true)}
            className="h-11 px-5 border-2 border-ink bg-brand text-white font-bold btn-press shadow-brutal-sm">
            Request resolution
          </button>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">This Call is still pending. Only the author can request resolution.</p>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="card-brutal border-2 border-ink sm:max-w-md" data-testid="resolution-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl font-bold">Request resolution</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger className="border-2 border-ink"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="called_it">✅ Called It</SelectItem>
                <SelectItem value="missed_it">❌ Missed It</SelectItem>
                <SelectItem value="partial">◐ Partially Correct</SelectItem>
                <SelectItem value="ambiguous">? Ambiguous</SelectItem>
              </SelectContent>
            </Select>
            <Textarea data-testid="resolution-explanation" placeholder="What happened?" rows={3}
              value={explanation} onChange={(e) => setExplanation(e.target.value)} className="border-2 border-ink" />
            <Input data-testid="resolution-evidence" placeholder="Evidence URL(s), space-separated"
              value={evidence} onChange={(e) => setEvidence(e.target.value)} className="border-2 border-ink" />
            <p className="text-xs text-muted-foreground">Success claims aren't auto-approved — they're verified before going public.</p>
            <button data-testid="submit-resolution-btn" disabled={busy || explanation.length < 3} onClick={request}
              className="w-full h-12 border-2 border-ink bg-ink text-white font-bold btn-press shadow-brutal-sm">
              Submit
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
